import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Stack,
  Link,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  IntegrationInstructions,
  Check,
  Warning,
  Settings,
  Launch,
  Refresh,
  Delete,
  Save,
} from '@mui/icons-material';
import api, { slackAPI } from '../services/api';

const WEBHOOK_URL_PATTERN = /^https:\/\/hooks\.slack\.com\/services\/[A-Za-z0-9/_-]+$/;

interface SlackIntegration {
  id: number;
  team_id?: string;
  team_name?: string;
  is_enabled: boolean;
  enabled?: boolean;
  status_enabled?: boolean;
  default_channel?: string;
  enable_conversation_notifications: boolean;
  enable_mention_notifications: boolean;
  enable_message_replies: boolean;
  enable_status_updates: boolean;
  notification_settings: Record<string, boolean>;
  channel_mappings: Record<string, string>;
  installed_at?: string | null;
  incoming_webhook_url?: string | null;
  webhook_url?: string | null;
  connection_mode?: 'webhook' | 'oauth' | string;
  mode?: string;
  has_webhook?: boolean;
  has_bot_token?: boolean;
}

interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_member: boolean;
}

const SlackIntegrationPage: React.FC = () => {
  const [integration, setIntegration] = useState<SlackIntegration | null>(null);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [webhookError, setWebhookError] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [formData, setFormData] = useState({
    connection_mode: 'webhook' as 'webhook' | 'oauth',
    webhook_url: '',
    is_enabled: true,
    client_id: '',
    client_secret: '',
    signing_secret: '',
    default_channel: '',
    enable_conversation_notifications: true,
    enable_mention_notifications: true,
    enable_message_replies: true,
    enable_status_updates: true,
    notification_settings: {
      new_conversation: true,
      conversation_assignment: true,
      mention_in_note: true,
      conversation_resolved: false,
      high_priority_conversation: true,
    },
  });

  useEffect(() => {
    fetchIntegration();
  }, []);

  const showToast = (message: string, severity: 'success' | 'error' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const validateWebhookUrl = (url: string, enabled: boolean): string => {
    const trimmed = (url || '').trim();
    if (enabled && !trimmed) {
      return 'Webhook URL is required when Enable Integration is ON';
    }
    if (trimmed && !WEBHOOK_URL_PATTERN.test(trimmed)) {
      return 'Webhook URL must be https://hooks.slack.com/services/...';
    }
    return '';
  };

  const isStatusEnabled = (data: SlackIntegration | null, override?: Partial<typeof formData>): boolean => {
    const mode = override?.connection_mode ?? data?.connection_mode ?? data?.mode ?? formData.connection_mode;
    const enabled = override?.is_enabled ?? data?.is_enabled ?? formData.is_enabled;
    const webhook = (
      override?.webhook_url ??
      data?.incoming_webhook_url ??
      data?.webhook_url ??
      formData.webhook_url ??
      ''
    ).trim();
    if (data?.status_enabled !== undefined && !override) {
      return Boolean(data.status_enabled);
    }
    if (mode === 'webhook') {
      return Boolean(enabled && webhook);
    }
    return Boolean(enabled && (data?.has_bot_token || data?.team_id));
  };

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await api.get('/slack/');
      const data: SlackIntegration = response.data;
      setIntegration(data);
      const mode = (data.connection_mode || data.mode || 'webhook') as 'webhook' | 'oauth';
      setFormData((prev) => ({
        ...prev,
        connection_mode: mode,
        webhook_url: data.incoming_webhook_url || data.webhook_url || '',
        is_enabled: data.is_enabled ?? data.enabled ?? false,
        default_channel: data.default_channel || '',
        enable_conversation_notifications: data.enable_conversation_notifications ?? true,
        enable_mention_notifications: data.enable_mention_notifications ?? true,
        enable_message_replies: data.enable_message_replies ?? true,
        enable_status_updates: data.enable_status_updates ?? true,
        notification_settings: {
          ...prev.notification_settings,
          ...(data.notification_settings || {}),
        },
      }));

      if (mode === 'oauth' && data.id > 0) {
        fetchChannels();
      }
    } catch (error) {
      console.error('Error fetching integration:', error);
      showToast('Failed to load Slack settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const data = await slackAPI.getChannels();
      setChannels(data.channels || []);
    } catch (error) {
      console.error('Error fetching channels:', error);
    }
  };

  const handleSaveWebhookSettings = async () => {
    const err = validateWebhookUrl(formData.webhook_url, formData.is_enabled);
    setWebhookError(err);
    if (err) {
      showToast(err, 'error');
      return;
    }

    try {
      setSaving(true);
      await api.put('/slack/', {
        webhook_url: formData.webhook_url.trim(),
        incoming_webhook_url: formData.webhook_url.trim(),
        enabled: formData.is_enabled,
        is_enabled: formData.is_enabled,
        mode: 'webhook',
        connection_mode: 'webhook',
        enable_conversation_notifications: formData.enable_conversation_notifications,
        enable_status_updates: formData.enable_status_updates,
        // Webhook cannot support replies/mentions — keep flags but default off when saving webhook
        enable_mention_notifications: false,
        enable_message_replies: false,
      });
      showToast('Slack webhook settings saved', 'success');
      await fetchIntegration();
    } catch (error: any) {
      console.error('Error saving webhook settings:', error);
      const detail = error?.response?.data?.detail || 'Failed to save settings';
      showToast(typeof detail === 'string' ? detail : JSON.stringify(detail), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateIntegration = async () => {
    try {
      await api.post('/slack/', {
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        signing_secret: formData.signing_secret,
        default_channel: formData.default_channel,
        enable_conversation_notifications: formData.enable_conversation_notifications,
        enable_mention_notifications: formData.enable_mention_notifications,
        enable_message_replies: formData.enable_message_replies,
        enable_status_updates: formData.enable_status_updates,
        connection_mode: 'oauth',
        mode: 'oauth',
        is_enabled: true,
      });

      setIsConfigDialogOpen(false);
      setActiveStep(1);
      showToast('OAuth app credentials saved', 'success');
      fetchIntegration();
    } catch (error: any) {
      console.error('Error creating integration:', error);
      const detail = error?.response?.data?.detail || 'Failed to create integration';
      showToast(typeof detail === 'string' ? detail : JSON.stringify(detail), 'error');
    }
  };

  const handleUpdateSettings = async () => {
    try {
      await api.put('/slack/', {
        default_channel: formData.default_channel,
        enable_conversation_notifications: formData.enable_conversation_notifications,
        enable_mention_notifications: formData.enable_mention_notifications,
        enable_message_replies: formData.enable_message_replies,
        enable_status_updates: formData.enable_status_updates,
        notification_settings: formData.notification_settings,
        connection_mode: 'oauth',
        mode: 'oauth',
      });
      showToast('Settings updated', 'success');
      fetchIntegration();
      setIsConfigDialogOpen(false);
    } catch (error: any) {
      console.error('Error updating settings:', error);
      showToast('Failed to update settings', 'error');
    }
  };

  const handleOAuthConnect = async () => {
    try {
      const response = await fetch(`/${process.env.REACT_APP_API_PATH}/slack/oauth/url`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.oauth_url, '_blank');
        setActiveStep(2);
      } else {
        showToast('Failed to get OAuth URL', 'error');
      }
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
      showToast('Failed to get OAuth URL', 'error');
    }
  };

  const handleTestIntegration = async () => {
    if (formData.connection_mode === 'webhook') {
      const err = validateWebhookUrl(formData.webhook_url, true);
      if (err && !integration?.has_webhook) {
        setWebhookError(err);
        showToast(err, 'error');
        return;
      }
    }

    try {
      setTesting(true);
      const data = await slackAPI.testIntegration();
      showToast(data?.message || 'Test message sent successfully', 'success');
      setActiveStep(3);
    } catch (error: any) {
      console.error('Error testing integration:', error);
      const detail = error?.response?.data?.detail || 'Test failed. Please check your configuration.';
      showToast(typeof detail === 'string' ? detail : JSON.stringify(detail), 'error');
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteIntegration = async () => {
    if (window.confirm('Are you sure you want to delete the Slack integration?')) {
      try {
        await api.delete('/slack/');
        setIntegration(null);
        setActiveStep(0);
        setFormData((prev) => ({
          ...prev,
          webhook_url: '',
          is_enabled: false,
          connection_mode: 'webhook',
        }));
        showToast('Slack integration removed', 'success');
      } catch (error) {
        console.error('Error deleting integration:', error);
        showToast('Failed to delete integration', 'error');
      }
    }
  };

  const steps = ['Create Slack App', 'Configure Integration', 'Connect to Slack', 'Test & Verify'];
  const webhookMode = formData.connection_mode === 'webhook';
  const statusEnabled = isStatusEnabled(integration, {
    connection_mode: formData.connection_mode,
    is_enabled: formData.is_enabled,
    webhook_url: formData.webhook_url,
  });
  const hasSavedIntegration = Boolean(integration && integration.id > 0);

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Slack Integration
        </Typography>
        {hasSavedIntegration && (
          <Stack direction="row" spacing={2}>
            {!webhookMode && (
              <Button variant="outlined" startIcon={<Settings />} onClick={() => setIsConfigDialogOpen(true)}>
                Settings
              </Button>
            )}
            <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleDeleteIntegration}>
              Remove Integration
            </Button>
          </Stack>
        )}
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardHeader title="Connection Mode" />
        <CardContent>
          <ToggleButtonGroup
            exclusive
            color="primary"
            value={formData.connection_mode}
            onChange={(_e, value) => {
              if (value) {
                setFormData((prev) => ({ ...prev, connection_mode: value }));
              }
            }}
          >
            <ToggleButton value="webhook">Incoming Webhook</ToggleButton>
            <ToggleButton value="oauth">Slack App (OAuth)</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {webhookMode
              ? 'Webhook mode posts messages to a fixed Slack channel. No Slack App install or bot token required.'
              : 'OAuth mode installs a Slack App for channels, replies, and slash commands.'}
          </Typography>
        </CardContent>
      </Card>

      {webhookMode ? (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={7}>
            <Card>
              <CardHeader title="Incoming Webhook Settings" avatar={<IntegrationInstructions color="primary" />} />
              <CardContent>
                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    label="Incoming Webhook URL"
                    value={formData.webhook_url}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, webhook_url: e.target.value }));
                      setWebhookError('');
                    }}
                    placeholder="https://hooks.slack.com/services/..."
                    error={Boolean(webhookError)}
                    helperText={
                      webhookError ||
                      'Paste the Incoming Webhook URL from your Slack app (channel is fixed by Slack).'
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_enabled}
                        onChange={(e) => setFormData((prev) => ({ ...prev, is_enabled: e.target.checked }))}
                      />
                    }
                    label="Enable Integration"
                  />
                  <Alert severity="info">
                    Status is <strong>Enabled</strong> only when a valid webhook URL is saved and Enable is ON.
                  </Alert>
                </Stack>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveWebhookSettings}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Launch />}
                  onClick={handleTestIntegration}
                  disabled={testing}
                >
                  {testing ? 'Testing...' : 'Test Connection'}
                </Button>
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card>
              <CardHeader title="Integration Status" avatar={<Settings color="primary" />} />
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Status</Typography>
                    <Chip
                      label={statusEnabled ? 'Enabled' : 'Disabled'}
                      color={statusEnabled ? 'success' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Mode</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      Incoming Webhook
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Channel</Typography>
                    <Typography variant="body2" fontWeight="medium">
                      Fixed by Slack webhook
                    </Typography>
                  </Box>
                  {hasSavedIntegration && (formData.webhook_url || integration?.has_webhook) && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Installed</Typography>
                      <Typography variant="body2">Configured</Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardHeader title="Feature Status" />
              <CardContent>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      {formData.enable_conversation_notifications ? <Check color="success" /> : <Warning color="warning" />}
                    </ListItemIcon>
                    <ListItemText primary="Conversation Notifications" secondary="Supported via webhook" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="disabled" />
                    </ListItemIcon>
                    <ListItemText primary="Message Replies" secondary="Not available in webhook mode" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Warning color="disabled" />
                    </ListItemIcon>
                    <ListItemText primary="Slash Commands" secondary="Requires Slack App (OAuth)" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <>
          {hasSavedIntegration ? (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Integration Status" avatar={<IntegrationInstructions color="primary" />} />
                  <CardContent>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">Status</Typography>
                        <Chip
                          label={statusEnabled ? 'Enabled' : 'Disabled'}
                          color={statusEnabled ? 'success' : 'default'}
                        />
                      </Box>
                      {integration?.team_name && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">Workspace</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {integration.team_name}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2">Default Channel</Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {integration?.default_channel || 'Not set'}
                        </Typography>
                      </Box>
                      {integration?.installed_at && integration.installed_at !== 'configured' && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2">Installed</Typography>
                          <Typography variant="body2">
                            {new Date(integration.installed_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                  <CardActions>
                    <Button size="small" startIcon={<Launch />} onClick={handleTestIntegration} disabled={testing}>
                      {testing ? 'Testing...' : 'Test Integration'}
                    </Button>
                    <Button size="small" startIcon={<Refresh />} onClick={fetchChannels}>
                      Refresh Channels
                    </Button>
                  </CardActions>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardHeader title="Feature Status" avatar={<Settings color="primary" />} />
                  <CardContent>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          {integration?.enable_conversation_notifications ? <Check color="success" /> : <Warning color="warning" />}
                        </ListItemIcon>
                        <ListItemText primary="Conversation Notifications" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {integration?.enable_mention_notifications ? <Check color="success" /> : <Warning color="warning" />}
                        </ListItemIcon>
                        <ListItemText primary="Mention Notifications" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {integration?.enable_message_replies ? <Check color="success" /> : <Warning color="warning" />}
                        </ListItemIcon>
                        <ListItemText primary="Message Replies" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          {integration?.enable_status_updates ? <Check color="success" /> : <Warning color="warning" />}
                        </ListItemIcon>
                        <ListItemText primary="Status Updates" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Card sx={{ mb: 3 }}>
              <CardHeader title="Slack App (OAuth) Setup" />
              <CardContent>
                <Stepper activeStep={activeStep} orientation="vertical">
                  {steps.map((label, index) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                      <StepContent>
                        {index === 0 && (
                          <Box>
                            <Typography variant="body2" paragraph>
                              First, create a Slack app in your workspace with the required permissions.
                            </Typography>
                            <Alert severity="info" sx={{ mb: 2 }}>
                              <Typography variant="body2">
                                Go to{' '}
                                <Link href="https://api.slack.com/apps" target="_blank">
                                  api.slack.com/apps
                                </Link>{' '}
                                and create a new app. Add OAuth scopes: <code>chat:write</code>,{' '}
                                <code>channels:read</code>, <code>groups:read</code>, <code>users:read</code>,{' '}
                                <code>commands</code>
                              </Typography>
                            </Alert>
                            <Button onClick={() => setIsConfigDialogOpen(true)} variant="contained">
                              Configure App Details
                            </Button>
                          </Box>
                        )}
                        {index === 1 && (
                          <Box>
                            <Typography variant="body2" paragraph>
                              Configure your Slack app credentials and settings.
                            </Typography>
                            <Button onClick={handleOAuthConnect} variant="contained">
                              Connect to Slack
                            </Button>
                          </Box>
                        )}
                        {index === 2 && (
                          <Box>
                            <Typography variant="body2" paragraph>
                              Complete the OAuth flow to connect your Slack workspace.
                            </Typography>
                            <Button onClick={handleTestIntegration} variant="contained">
                              Test Integration
                            </Button>
                          </Box>
                        )}
                        {index === 3 && (
                          <Box>
                            <Typography variant="body2" paragraph>
                              Your Slack integration is ready! You&apos;ll receive notifications in your configured
                              channel.
                            </Typography>
                            <Alert severity="success">Integration setup complete!</Alert>
                          </Box>
                        )}
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader title="Available Slack Commands" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Slash Commands
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="/converse help" secondary="Show available commands" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="/converse status" secondary="Show your conversation status" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="/converse stats" secondary="Show quick statistics" />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Bot Mentions
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText primary="@converse help" secondary="Show help message" />
                    </ListItem>
                    <ListItem>
                      <ListItemText primary="@converse status" secondary="Your current status" />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={isConfigDialogOpen} onClose={() => setIsConfigDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {hasSavedIntegration ? 'Update Slack Integration' : 'Configure Slack Integration'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              {!hasSavedIntegration && (
                <>
                  <Grid item xs={12}>
                    <Alert severity="info">
                      You&apos;ll need these values from your Slack app settings at{' '}
                      <Link href="https://api.slack.com/apps" target="_blank">
                        api.slack.com/apps
                      </Link>
                    </Alert>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Client ID"
                      value={formData.client_id}
                      onChange={(e) => setFormData((prev) => ({ ...prev, client_id: e.target.value }))}
                      helperText="Found in Basic Information > App Credentials"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Client Secret"
                      type="password"
                      value={formData.client_secret}
                      onChange={(e) => setFormData((prev) => ({ ...prev, client_secret: e.target.value }))}
                      helperText="Found in Basic Information > App Credentials"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Signing Secret"
                      type="password"
                      value={formData.signing_secret}
                      onChange={(e) => setFormData((prev) => ({ ...prev, signing_secret: e.target.value }))}
                      helperText="Found in Basic Information > App Credentials"
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Default Channel</InputLabel>
                  <Select
                    value={formData.default_channel}
                    label="Default Channel"
                    onChange={(e) => setFormData((prev) => ({ ...prev, default_channel: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {channels.map((channel) => (
                      <MenuItem key={channel.id} value={`#${channel.name}`}>
                        #{channel.name} {channel.is_private && '(Private)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Feature Settings
                </Typography>
                <Stack spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_conversation_notifications}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            enable_conversation_notifications: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Conversation Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_mention_notifications}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            enable_mention_notifications: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Mention Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_message_replies}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            enable_message_replies: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Message Replies"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.enable_status_updates}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            enable_status_updates: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Status Updates"
                  />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfigDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={hasSavedIntegration ? handleUpdateSettings : handleCreateIntegration}
            variant="contained"
          >
            {hasSavedIntegration ? 'Update Settings' : 'Create Integration'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SlackIntegrationPage;
